/**
 * LabelView - 标签查看页面（公开访问，用于二维码扫描）
 * 
 * 路由：/view/:labelId
 * 特点：
 * - 无需登录
 * - 只读展示
 * - 移动端优化
 * - 从API获取数据
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Caution } from '@icon-park/react';
import { fetchLabelDetail, fetchContentItem, fetchLabelVoice, generateLabelVoice } from '../../api/labels';
import { ENV_CONFIG } from '../../config';
import LabelCanvas from './LabelCanvas';
import LabelSkeleton from '../../components/common/LabelSkeleton';
import { getProxyUrl } from '../../utils/urlProxy';
import './styles/LabelView.css';

// lite模式下所有内容都需要懒加载（后端不返回content_data以提升速度）

function LabelView() {
  // 从URL获取labelId
  const labelId = getLabelIdFromURL();
  const isPhonePreview = new URLSearchParams(window.location.search).get('mobile') === '1';
  const enableVoiceGenerate = ENV_CONFIG?.IS_DEV || new URLSearchParams(window.location.search).get('voice') === '1';

  const [labelData, setLabelData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [voice, setVoice] = useState(null);
  const [voiceLoading, setVoiceLoading] = useState(false);
  const [voiceGenerating, setVoiceGenerating] = useState(false);
  // 用于追踪已加载内容项数量，触发批量更新
  const [loadedCount, setLoadedCount] = useState(0);
  // 用ref存储内容数据，避免频繁setState
  const contentDataRef = useRef({});

  // 记录滚动位置变化
  const lastScrollRef = useRef(0);
  useEffect(() => {
    const handleScroll = () => {
      const currentScroll = window.scrollY;
      if (Math.abs(currentScroll - lastScrollRef.current) > 50) {
        console.log(`[Scroll] 位置变化: ${lastScrollRef.current} -> ${currentScroll}`);
        if (currentScroll === 0 && lastScrollRef.current > 100) {
          console.warn('[Scroll] ⚠️ 检测到滚动位置被重置到顶部！');
          console.trace('[Scroll] 调用堆栈');
        }
      }
      lastScrollRef.current = currentScroll;
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // 合并后的labelData（框架 + 已加载的内容）- 使用useMemo缓存
  const mergedLabelData = useMemo(() => {
    if (!labelData) return null;
    console.log(`[LabelView] 合并数据, loadedCount=${loadedCount}`);
    return {
      ...labelData,
      contentItems: labelData.contentItems?.map(item => ({
        ...item,
        contentData: contentDataRef.current[item.itemId] ?? item.contentData
      }))
    };
  }, [labelData, loadedCount]); // loadedCount变化时重新计算

  const loadVoice = async (id) => {
    try {
      setVoiceLoading(true);
      const data = await fetchLabelVoice(id);
      if (data && data.fileUrl) {
        setVoice({
          ...data,
          proxiedUrl: getProxyUrl(data.fileUrl)
        });
      } else {
        setVoice(null);
      }
    } catch (err) {
      console.warn('[LabelView] 获取语音失败：', err?.message || err);
      setVoice(null);
    } finally {
      setVoiceLoading(false);
    }
  };

  const handleGenerateVoice = async () => {
    const resolvedLabelId = labelData?.id || labelId;
    if (!resolvedLabelId) return;
    const text = (labelData?.meta && labelData.meta.voiceText) || labelData?.labelName || '数字标签语音';
    try {
      setVoiceGenerating(true);
      await generateLabelVoice(resolvedLabelId, { text });
      await loadVoice(resolvedLabelId);
    } catch (err) {
      console.error('[LabelView] 生成语音失败:', err?.message || err);
    } finally {
      setVoiceGenerating(false);
    }
  };

  useEffect(() => {
    const loadLabel = async () => {
      try {
        setLoading(true);
        setError(null);
        contentDataRef.current = {}; // 重置

        // Mock模式下，优先从URL参数解码数据（支持跨设备预览）
        const params = new URLSearchParams(window.location.search);
        const hasEncoded = params.get('mock') === '1' && params.get('d');
        if (ENV_CONFIG?.USE_MOCK && hasEncoded) {
          try {
            const encoded = params.get('d');
            const json = decodeURIComponent(escape(atob(encoded)));
            const parsed = JSON.parse(json);
            setLabelData(parsed);
            return;
          } catch (e) {
            console.warn('解码URL内置标签数据失败，回退到本地存储', e);
          }
        }

        console.log(`[LabelView] 开始加载标签(lite模式): labelId=${labelId}`);
        const data = await fetchLabelDetail(labelId, true, { skipTenant: true });
        console.log('[LabelView] lite模式数据加载成功:', {
          labelName: data.labelName,
          contentItemsCount: data.contentItems?.length
        });
        setLabelData(data);
        const resolvedLabelId = data?.id || labelId;
        loadVoice(resolvedLabelId);

        // 2. 后台并行加载所有内容项
        if (data.contentItems) {
          const itemsToLoad = data.contentItems.filter(item => item.contentData === null);
          console.log(`[LabelView] 需要懒加载的内容项: ${itemsToLoad.length}个`);

          let loadedItems = 0;
          const totalItems = itemsToLoad.length;

          itemsToLoad.forEach(item => {
            fetchContentItem(resolvedLabelId, item.itemId, { skipTenant: true }).then(fullItem => {
              if (fullItem && fullItem.contentData) {
                // 存入ref，不立即触发渲染
                contentDataRef.current[item.itemId] = fullItem.contentData;
                loadedItems++;

                // 每加载3个或全部加载完成时，批量触发一次渲染
                if (loadedItems % 3 === 0 || loadedItems === totalItems) {
                  setLoadedCount(loadedItems);
                }
              }
            }).catch(err => {
              console.error(`[LabelView] 加载内容项失败: itemId=${item.itemId}`, err);
              loadedItems++;
              if (loadedItems === totalItems) {
                setLoadedCount(loadedItems);
              }
            });
          });
        }
      } catch (err) {
        console.error('加载标签失败:', err);
        setError(err.message || '标签不存在或已被删除');
      } finally {
        setLoading(false);
      }
    };

    if (labelId) {
      loadLabel();
    } else {
      setError('无效的标签ID');
      setLoading(false);
    }
  }, [labelId]);

  // 加载中 - 显示骨架屏
  if (loading) {
    return (
      <div className={`label-view-container ${isPhonePreview ? 'phone-preview' : ''}`}>
        <LabelSkeleton />
      </div>
    );
  }

  // 错误状态
  if (error) {
    return (
      <div className={`label-view-container ${isPhonePreview ? 'phone-preview' : ''}`}>
        <div className="label-view-error">
          <div className="error-icon"><Caution theme="filled" size={48} fill="#faad14" /></div>
          <h2>加载失败</h2>
          <p>{error}</p>
          <button 
            className="btn-retry" 
            onClick={() => window.location.reload()}
          >
            重试
          </button>
        </div>
      </div>
    );
  }

  // 渲染标签内容（使用合并后的数据，包含懒加载的内容）
  return (
    <div className={`label-view-container ${isPhonePreview ? 'phone-preview' : ''}`}>
      {voice?.fileUrl && (
        <div className="label-voice-player">
          <div className="voice-header">
            <span className="voice-title">语音讲解</span>
            {voiceLoading && <span className="voice-hint">加载中...</span>}
          </div>
          <audio
            src={voice.proxiedUrl || voice.fileUrl}
            controls
            preload="metadata"
            className="voice-audio"
            onError={() => console.error('[LabelView] 语音播放失败', voice?.fileUrl)}
          />
        </div>
      )}

      {!voice?.fileUrl && enableVoiceGenerate && (
        <div className="label-voice-player">
          <div className="voice-header">
            <span className="voice-title">生成语音</span>
            <button
              className="voice-generate-btn"
              onClick={handleGenerateVoice}
              disabled={voiceGenerating}
            >
              {voiceGenerating ? '生成中...' : '一键生成'}
            </button>
          </div>
          <p className="voice-hint">使用标签名称生成语音（需已登录并具备编辑标签权限）。</p>
        </div>
      )}

      <LabelCanvas data={mergedLabelData} phonePreview={isPhonePreview} />
    </div>
  );
}

/**
 * 从URL获取labelId
 * 支持：/view/abc123 或 ?id=abc123
 */
function getLabelIdFromURL() {
  const path = window.location.pathname;
  const searchParams = new URLSearchParams(window.location.search);
  
  // 从路径获取: /view/abc123
  const pathMatch = path.match(/\/view\/([^/]+)/);
  if (pathMatch) {
    return pathMatch[1];
  }
  
  // 从查询参数获取: ?id=abc123
  return searchParams.get('id') || searchParams.get('labelId');
}

// ✅ 所有渲染逻辑已移至共享组件 label-content/
// 现在预览和实际查看使用完全相同的渲染逻辑，确保所见即所得！

export default LabelView;
